import { useCallback, useContext, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
} from '@mui/material'
import AccessTime from '@mui/icons-material/AccessTime'
import CheckCircle from '@mui/icons-material/CheckCircle'
import Pause from '@mui/icons-material/Pause'
import PlayArrow from '@mui/icons-material/PlayArrow'
import { styled } from '@mui/material/styles'
import { ProfileContext } from 'src/consumer/context/ProfileContext'
import { hasCompletedConsumerOnboarding } from 'src/consumer/helpers/onboardingStatus'
import { getAllRolesInBoolean, getUserRoles } from 'src/mentor/layout/utils'
import {
  getAvailablePortals,
  getPortalFlowKey,
  PORTAL_IDS,
} from 'src/shared/config/portalConfig'
import FormNavbar from '../Form/components/FormNavbar'

const BackgroundContainer = styled(Box)(() => ({
  width: '100%',
  minHeight: '100vh',
  background: 'linear-gradient(135deg, rgba(249, 250, 251, 0.95) 0%, rgba(243, 244, 246, 0.98) 100%)',
  position: 'relative',
  overflow: 'auto',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'radial-gradient(circle at 20% 50%, rgba(139, 92, 246, 0.08) 0%, transparent 50%), radial-gradient(circle at 80% 80%, rgba(99, 102, 241, 0.06) 0%, transparent 50%)',
    pointerEvents: 'none',
  },
}))

const ContentContainer = styled(Container)(({ theme }) => ({
  position: 'relative',
  zIndex: 1,
  paddingTop: theme.spacing(8),
  paddingBottom: theme.spacing(6),
  [theme.breakpoints.down('sm')]: {
    paddingTop: theme.spacing(6),
    paddingBottom: theme.spacing(4),
  },
}))

const HeaderSection = styled(Box)(({ theme }) => ({
  textAlign: 'center',
  marginBottom: theme.spacing(6),
  [theme.breakpoints.down('sm')]: {
    marginBottom: theme.spacing(4),
  },
}))

const SuperText = styled(Typography)(({ theme }) => ({
  fontFamily: "'Inter', sans-serif",
  fontSize: '14px',
  fontWeight: 600,
  letterSpacing: '0.1em',
  textTransform: 'uppercase',
  color: 'rgba(99, 102, 241, 0.8)',
  marginBottom: theme.spacing(1),
  [theme.breakpoints.down('sm')]: {
    fontSize: '12px',
  },
}))

const MainHeading = styled(Typography)(({ theme }) => ({
  fontFamily: "'Inter', sans-serif",
  fontSize: '42px',
  fontWeight: 700,
  lineHeight: 1.2,
  color: '#1f2937',
  marginBottom: theme.spacing(1.5),
  background: 'linear-gradient(135deg, #1f2937 0%, #4b5563 100%)',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  backgroundClip: 'text',
  [theme.breakpoints.down('md')]: {
    fontSize: '36px',
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '28px',
  },
}))

const SubHeading = styled(Typography)(({ theme }) => ({
  fontFamily: "'Inter', sans-serif",
  fontSize: '18px',
  fontWeight: 400,
  color: '#6b7280',
  marginBottom: theme.spacing(2),
  [theme.breakpoints.down('sm')]: {
    fontSize: '16px',
  },
}))

const AutoSelectText = styled(Box)(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  padding: theme.spacing(1, 2),
  borderRadius: theme.shape.borderRadius * 2,
  background: 'rgba(99, 102, 241, 0.1)',
  border: '1px solid rgba(99, 102, 241, 0.2)',
  fontFamily: "'Inter', sans-serif",
  fontSize: '14px',
  fontWeight: 500,
  color: '#6366f1',
  marginTop: theme.spacing(1),
}))

const PauseButton = styled(Button)(({ theme }) => ({
  minWidth: 'auto',
  padding: theme.spacing(0.5, 1),
  marginLeft: theme.spacing(1),
  color: '#6366f1',
  fontSize: '12px',
  textTransform: 'none',
  '&:hover': {
    background: 'rgba(99, 102, 241, 0.1)',
  },
}))

const PortalGrid = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
  gap: theme.spacing(3),
  maxWidth: '1400px',
  margin: '0 auto',
  [theme.breakpoints.down('sm')]: {
    gridTemplateColumns: '1fr',
    gap: theme.spacing(2),
  },
}))

const PortalCard = styled(Card, {
  shouldForwardProp: (prop) => prop !== 'isDefault',
})(({ isDefault }) => ({
  position: 'relative',
  borderRadius: '20px',
  background: 'rgba(255, 255, 255, 0.85)',
  backdropFilter: 'blur(20px) saturate(180%)',
  WebkitBackdropFilter: 'blur(20px) saturate(180%)',
  border: isDefault
    ? '2px solid rgba(99, 102, 241, 0.4)'
    : '1px solid rgba(255, 255, 255, 0.5)',
  boxShadow: isDefault
    ? '0 8px 32px rgba(99, 102, 241, 0.2), 0 2px 8px rgba(0, 0, 0, 0.1)'
    : '0 4px 20px rgba(0, 0, 0, 0.08), 0 1px 4px rgba(0, 0, 0, 0.05)',
  cursor: 'pointer',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '4px',
    background: isDefault
      ? 'linear-gradient(90deg, rgba(99, 102, 241, 0.8) 0%, rgba(139, 92, 246, 0.8) 100%)'
      : 'linear-gradient(90deg, rgba(99, 102, 241, 0.3) 0%, rgba(139, 92, 246, 0.3) 100%)',
    opacity: isDefault ? 1 : 0,
    transition: 'opacity 0.3s ease',
  },
  '&:hover': {
    transform: 'translateY(-8px) scale(1.02)',
    boxShadow: '0 12px 40px rgba(99, 102, 241, 0.25), 0 4px 12px rgba(0, 0, 0, 0.15)',
    borderColor: 'rgba(99, 102, 241, 0.5)',
    '&::before': {
      opacity: 1,
    },
  },
  '&:active': {
    transform: 'translateY(-4px) scale(1.01)',
  },
}))

const CardContentStyled = styled(CardContent)(({ theme }) => ({
  padding: theme.spacing(3.5),
  '&:last-child': {
    paddingBottom: theme.spacing(3.5),
  },
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(2.5),
    '&:last-child': {
      paddingBottom: theme.spacing(2.5),
    },
  },
}))

const IconContainer = styled(Box)(({ theme, gradient }) => ({
  width: 72,
  height: 72,
  borderRadius: '18px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginBottom: theme.spacing(2.5),
  background: gradient || 'linear-gradient(135deg, rgba(99, 102, 241, 0.15) 0%, rgba(139, 92, 246, 0.15) 100%)',
  boxShadow: '0 4px 12px rgba(99, 102, 241, 0.2)',
  [theme.breakpoints.down('sm')]: {
    width: 64,
    height: 64,
    marginBottom: theme.spacing(2),
  },
}))

const PortalTitle = styled(Typography)(({ theme }) => ({
  fontFamily: "'Inter', sans-serif",
  fontSize: '22px',
  fontWeight: 700,
  color: '#1f2937',
  marginBottom: theme.spacing(1),
  lineHeight: 1.3,
  [theme.breakpoints.down('sm')]: {
    fontSize: '20px',
  },
}))

const PortalDescription = styled(Typography)(({ theme }) => ({
  fontFamily: "'Inter', sans-serif",
  fontSize: '14px',
  fontWeight: 400,
  color: '#6b7280',
  lineHeight: 1.6,
  marginBottom: theme.spacing(2),
  minHeight: '44px',
}))

const DefaultBadge = styled(Chip)(({ theme }) => ({
  position: 'absolute',
  top: theme.spacing(2),
  right: theme.spacing(2),
  background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.95) 0%, rgba(139, 92, 246, 0.95) 100%)',
  color: 'white',
  fontWeight: 600,
  fontSize: '11px',
  height: '24px',
  border: 'none',
  boxShadow: '0 2px 8px rgba(99, 102, 241, 0.3)',
}))

const StatusChip = styled(Chip)(({ theme }) => ({
  marginTop: theme.spacing(1),
  fontSize: '12px',
  height: '26px',
  fontWeight: 500,
  backgroundColor: 'rgba(234, 179, 8, 0.1)',
  color: '#d97706',
  border: '1px solid rgba(234, 179, 8, 0.2)',
}))

const PORTAL_CHOOSER_DETAILS_BY_ID = {
  [PORTAL_IDS.CONSUMER]: {
    title: 'Wellness App',
    description: 'Explore courses, assessments, journals, and wellness content',
    gradient: 'linear-gradient(135deg, rgba(99, 102, 241, 0.15) 0%, rgba(139, 92, 246, 0.15) 100%)',
    iconColor: '#6366f1',
    getStatus: (availableRoles, profileData) => {
      if (profileData && !hasCompletedConsumerOnboarding(profileData)) {
        return 'Complete Onboarding'
      }
      return null
    },
  },
  [PORTAL_IDS.MENTOR]: {
    title: 'Mentor Portal',
    description: 'Manage appointments, availability, and patient interactions',
    gradient: 'linear-gradient(135deg, rgba(59, 130, 246, 0.15) 0%, rgba(37, 99, 235, 0.15) 100%)',
    iconColor: '#3b82f6',
    canAutoSelect: (availableRoles) =>
      availableRoles.isTeacher ||
      availableRoles.isDoctor ||
      availableRoles.isTherapist ||
      availableRoles.isScreener,
    getTitle: (availableRoles) => {
      return availableRoles.isDoctor ? 'Doctor Portal' : 'Therapist Portal'
    },
    getStatus: (availableRoles) => {
      if (!availableRoles.isTeacher) {
        return 'KYC Pending'
      }
      return null
    },
  },
  [PORTAL_IDS.CLINIC]: {
    title: 'Clinic Management',
    description: 'Manage clinic locations, staff, calendars, and operations',
    gradient: 'linear-gradient(135deg, rgba(16, 185, 129, 0.15) 0%, rgba(5, 150, 105, 0.15) 100%)',
    iconColor: '#10b981',
    getStatus: (availableRoles) => {
      if (availableRoles.isClinicProspect) {
        return 'Onboarding Pending'
      }
      return null
    },
  },
  [PORTAL_IDS.CLINIC_ASSISTANT]: {
    title: 'Clinic Assistant',
    description: 'Manage bookings, appointments, and front desk operations',
    gradient: 'linear-gradient(135deg, rgba(236, 72, 153, 0.15) 0%, rgba(219, 39, 119, 0.15) 100%)',
    iconColor: '#ec4899',
  },
  [PORTAL_IDS.KIOSK]: {
    title: 'Kiosk (AUMHUM Screen)',
    description: 'Doctor video-call screens in clinic chambers',
    gradient: 'linear-gradient(135deg, rgba(34, 197, 94, 0.15) 0%, rgba(22, 163, 74, 0.15) 100%)',
    iconColor: '#22c55e',
  },
  [PORTAL_IDS.DISPLAY]: {
    title: 'Display (OOH / Token)',
    description: 'Out-of-home appointment / token screens in clinic waiting areas',
    gradient: 'linear-gradient(135deg, rgba(14, 165, 233, 0.15) 0%, rgba(2, 132, 199, 0.15) 100%)',
    iconColor: '#0ea5e9',
  },
  [PORTAL_IDS.BUSINESS]: {
    title: 'Business Portal',
    description: 'Manage team wellness, subscriptions, and organizational insights',
    gradient: 'linear-gradient(135deg, rgba(245, 158, 11, 0.15) 0%, rgba(217, 119, 6, 0.15) 100%)',
    iconColor: '#f59e0b',
  },
  [PORTAL_IDS.PATIENT]: {
    title: 'Patient App',
    description: 'Review care plans, assigned specialists, appointments, and clinical updates',
    gradient: 'linear-gradient(135deg, rgba(14, 165, 233, 0.15) 0%, rgba(2, 132, 199, 0.15) 100%)',
    iconColor: '#0284c7',
  },
  [PORTAL_IDS.PATIENT_PROSPECT]: {
    title: 'Care Request',
    description: 'Continue intake, reports, matching, and coordinator updates for your care request',
    gradient: 'linear-gradient(135deg, rgba(20, 184, 166, 0.15) 0%, rgba(13, 148, 136, 0.15) 100%)',
    iconColor: '#0d9488',
  },
  [PORTAL_IDS.SUPPORT]: {
    title: 'Support Portal',
    description: 'Manage patients, practitioners, clinics, and support operations',
    gradient: 'linear-gradient(135deg, rgba(139, 92, 246, 0.15) 0%, rgba(124, 58, 237, 0.15) 100%)',
    iconColor: '#8b5cf6',
  },
  [PORTAL_IDS.CONTROL_CENTER]: {
    title: 'Control Center',
    description: 'Administrative access to manage the AUMHUM platform',
    gradient: 'linear-gradient(135deg, rgba(239, 68, 68, 0.15) 0%, rgba(220, 38, 38, 0.15) 100%)',
    iconColor: '#ef4444',
  },
}

function getChooserPortal(registeredPortal) {
  const details = PORTAL_CHOOSER_DETAILS_BY_ID[registeredPortal.id]
  if (!details) return null

  return {
    ...registeredPortal,
    ...details,
    icon: registeredPortal.icon,
    path: registeredPortal.path,
    title: details.title || registeredPortal.name,
  }
}

const getFlowKey = (portalId) => getPortalFlowKey(portalId)

export const FlowSelectionComponent = ({ showNormalUserView, preferredPortalId }) => {
  const navigate = useNavigate()
  const availableRoles = getAllRolesInBoolean() || {}
  const userRoles = getUserRoles() || []
  const { profileData } = useContext(ProfileContext)
  const [secondsRemainingInAutoSelect, setSecondsRemainingInAutoSelect] = useState(10)
  const [isTimerPaused, setIsTimerPaused] = useState(false)

  const availablePortals = getAvailablePortals(userRoles)
    .map(getChooserPortal)
    .filter(Boolean)
  const canAutoSelectPortal = (portal) => portal?.canAutoSelect?.(availableRoles) ?? true

  const handlePortalSelection = useCallback((portal) => {
    if (!portal) return

    if (portal.id === PORTAL_IDS.CONSUMER) {
      sessionStorage.setItem('explicitlyChoseConsumerPortal', 'true')
      sessionStorage.setItem('chosenSomeFlowInThisSession', true)
      localStorage.removeItem('lastChosenFlow')
      showNormalUserView()
      return
    }

    sessionStorage.setItem('chosenSomeFlowInThisSession', true)

    const flowKey = getFlowKey(portal.id)
    if (flowKey) {
      localStorage.setItem('lastChosenFlow', flowKey)
    }

    if (portal.external) {
      window.location.href = portal.path
      return
    }

    navigate(portal.path)
  }, [navigate, showNormalUserView])

  const resolveDefaultFlowCandidate = (flowKey) => {
    if (!flowKey || flowKey === PORTAL_IDS.CONSUMER) return null

    const portal = availablePortals.find((availablePortal) => getFlowKey(availablePortal.id) === flowKey)
    if (!portal || !canAutoSelectPortal(portal)) return null

    return flowKey
  }

  const lastChosenFlowRaw = localStorage.getItem('lastChosenFlow')
  const storedDefaultFlow = resolveDefaultFlowCandidate(lastChosenFlowRaw)
  const rolePreferredFlow = resolveDefaultFlowCandidate(preferredPortalId)
  const defaultFlow = storedDefaultFlow || rolePreferredFlow

  // Clean up stale local defaults so old sessions cannot send users into
  // portals their current roles do not grant.
  useEffect(() => {
    if (lastChosenFlowRaw && lastChosenFlowRaw !== storedDefaultFlow) {
      localStorage.removeItem('lastChosenFlow')
    }
  }, [lastChosenFlowRaw, storedDefaultFlow])

  // Check if user has consumer flow
  const hasConsumerFlow = availablePortals.some((portal) => portal.id === PORTAL_IDS.CONSUMER)
  const nonConsumerPortals = availablePortals.filter((portal) => portal.id !== PORTAL_IDS.CONSUMER)

  // If user has multiple portals (including consumer), always show flow selection
  // If user has exactly 2 flows and one is consumer, auto-select the non-consumer one
  useEffect(() => {
    if (availablePortals.length === 2 && hasConsumerFlow && nonConsumerPortals.length === 1) {
      const nonConsumerPortal = nonConsumerPortals[0]
      if (!canAutoSelectPortal(nonConsumerPortal)) {
        return
      }
      handlePortalSelection(nonConsumerPortal)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availablePortals.length, hasConsumerFlow, nonConsumerPortals.length])

  // Auto-select timeout - only runs when timer reaches 0 and is not paused
  // Never auto-select consumer portal - only non-consumer portals
  useEffect(() => {
    if (isTimerPaused || secondsRemainingInAutoSelect > 0 || !defaultFlow) return

    const defaultPortal = availablePortals.find(
      (portal) => getFlowKey(portal.id) === defaultFlow,
    )
    if (defaultPortal && canAutoSelectPortal(defaultPortal)) {
      handlePortalSelection(defaultPortal)
    }
    // Never auto-select consumer - if no valid default, user must choose manually
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultFlow, navigate, secondsRemainingInAutoSelect, isTimerPaused])

  // Countdown timer - decrements every second when not paused
  useEffect(() => {
    if (isTimerPaused || secondsRemainingInAutoSelect <= 0) return

    const intervalId = setInterval(() => {
      setSecondsRemainingInAutoSelect((prevSeconds) => {
        if (prevSeconds > 1) {
          return prevSeconds - 1
        } else {
          return 0
        }
      })
    }, 1000)

    return () => clearInterval(intervalId)
  }, [isTimerPaused, secondsRemainingInAutoSelect])

  // Auto-select if only one portal available (and it's not consumer)
  useEffect(() => {
    if (availablePortals.length === 1) {
      const portal = availablePortals[0]
      // Only auto-select if it's not consumer
      if (portal.id !== PORTAL_IDS.CONSUMER && canAutoSelectPortal(portal)) {
        handlePortalSelection(portal)
      }
      // If only consumer portal is available, show it (user has no other choice)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <BackgroundContainer>
      <FormNavbar
        goBackFunction={() => null}
        backVisible={false}
        languageSelectVisible={true}
        logoutDropDownVisible={true}
      />

      <ContentContainer maxWidth="lg">
        <HeaderSection>
          <SuperText>Welcome to AUMHUM</SuperText>
          <MainHeading>
            Choose Your Journey, {profileData?.name?.split(' ')[0] || 'User'}
          </MainHeading>
          <SubHeading>Select the portal that matches your role and needs</SubHeading>
          {defaultFlow &&
            defaultFlow !== PORTAL_IDS.CONSUMER &&
            availablePortals.length > 1 &&
            !(availablePortals.length === 2 && hasConsumerFlow && nonConsumerPortals.length === 1) && (
            <AutoSelectText>
              <AccessTime sx={{ fontSize: 16 }} />
              Auto-selecting default in {secondsRemainingInAutoSelect} seconds...
              <PauseButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation()
                  setIsTimerPaused(!isTimerPaused)
                }}
                startIcon={isTimerPaused ? <PlayArrow sx={{ fontSize: 14 }} /> : <Pause sx={{ fontSize: 14 }} />}
              >
                {isTimerPaused ? 'Resume' : 'Pause'}
              </PauseButton>
            </AutoSelectText>
          )}
        </HeaderSection>

        <PortalGrid>
          {availablePortals.map((portal) => {
            const Icon = portal.icon
            const isDefault = getFlowKey(portal.id) === defaultFlow
            const status = portal.getStatus?.(availableRoles, profileData)
            const title = portal.getTitle?.(availableRoles) || portal.title

            return (
              <PortalCard
                key={portal.id}
                isDefault={isDefault && portal.id !== PORTAL_IDS.CONSUMER}
                onClick={() => handlePortalSelection(portal)}
              >
                {isDefault && portal.id !== PORTAL_IDS.CONSUMER && (
                  <DefaultBadge label="Default" icon={<CheckCircle />} />
                )}
                <CardContentStyled>
                  <IconContainer gradient={portal.gradient}>
                    <Icon sx={{ fontSize: 36, color: portal.iconColor }} />
                  </IconContainer>
                  <PortalTitle>{title}</PortalTitle>
                  <PortalDescription>{portal.description}</PortalDescription>
                  {status && <StatusChip label={status} size="small" />}
                </CardContentStyled>
              </PortalCard>
            )
          })}
        </PortalGrid>
      </ContentContainer>
    </BackgroundContainer>
  )
}
